import { Source } from 'api3';
import { AsyncPipe } from '@angular/common';
import { Component, ViewChild, computed, inject, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { QiraDataProvider, QiraSubject } from '@app/services/types';
import { SourceLinkLogoComponent } from '../../source-link/source-link-logo.component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { ListboxModule } from 'primeng/listbox';
import { Popover } from 'primeng/popover';
import { TextareaModule } from 'primeng/textarea';

export const DocumentTypeOptions: Source['subject'][] = [
  QiraSubject.Adjustments,
  // QiraSubject.Battery,
  QiraSubject.Bulletins,
  // QiraSubject.ControlUnitLocations,
  QiraSubject.Default,
  // QiraSubject.EngineLocations,
  // QiraSubject.EOBDLocations,
  QiraSubject.ErrorCode,
  // QiraSubject.FuseBoxes,
  QiraSubject.FuseLocations,
  // QiraSubject.GroundingPointLocations,
  // QiraSubject.HVACLocations,
  // QiraSubject.Lubricants,
  // QiraSubject.MaintenanceForms,
  // QiraSubject.MaintenancePeriods,
  // QiraSubject.MaintenanceProcesses,
  // QiraSubject.MaintenanceServiceResets,
  // QiraSubject.Recalls,
  QiraSubject.RepairManuals,
  // QiraSubject.SmartCases,
  // QiraSubject.TechnicalDrawings,
  // QiraSubject.WarningLights,
  QiraSubject.WiringDiagrams,
  QiraSubject.RepairTimes,
];

export const ProviderOptions: Source['provider'][] = [
  QiraDataProvider.HGS,
  QiraDataProvider.Mercedes,
  QiraDataProvider.Varta,
  QiraDataProvider.Volkswagen,
  QiraDataProvider.Skoda,
  // QiraDataProvider.QiraDB,
  // QiraDataProvider.Static,
  // QiraDataProvider.HaynesPro,
];

@Component({
  selector: 'app-smart-search-filter',
  templateUrl: './smart-search-filter.component.html',
  imports: [TranslocoPipe, AsyncPipe, ButtonModule, FormsModule, TextareaModule, SourceLinkLogoComponent, Popover, ListboxModule],
  standalone: true,
})
export class SmartSearchFilterComponent {
  @ViewChild('filterPopover') filterPopover!: Popover;

  selectedProviders = model<Source['provider'][]>([]);
  selectedDocumentTypes = model<Source['subject'][]>([]);

  translocoService = inject(TranslocoService);

  protected _filterValues: any[] = [];

  // We give a subset of all possible options to the user
  allFilterOptions = computed(async () => {
    return [
      {
        label: await this.translocoService.translate('chatMessages.filter.option.documentType'),
        items: await this._getDocumentTypeOptions(),
      },
      {
        label: await this.translocoService.translate('chatMessages.filter.option.provider'),
        items: await this._getProviderOptions(),
      },
    ];
  });

  toggle(event: Event) {
    this.filterPopover.toggle(event);
    event.stopPropagation();
    return false;
  }

  closePopover() {
    if (this.filterPopover.overlayVisible) {
      this.filterPopover.hide();
    }
  }

  onFilterChange() {
    this.selectedDocumentTypes.set(this._filterValues.filter((v) => v.type === 'documentType').map((f) => f.value));
    this.selectedProviders.set(this._filterValues.filter((v) => v.type === 'provider').map((f) => f.value));
  }

  removeFilterValue(value: any, type: 'documentType' | 'provider') {
    const filteredValues = this._filterValues.filter((v) => v.value !== value);
    if (type === 'documentType') {
      this.selectedDocumentTypes.set(filteredValues.filter((v) => v.type === 'documentType').map((f) => f.value));
    } else {
      this.selectedProviders.set(filteredValues.filter((v) => v.type === 'provider').map((f) => f.value));
    }
  }

  clearAllFilters() {
    this._filterValues = [];
    this.selectedDocumentTypes.set([]);
    this.selectedProviders.set([]);
  }

  private async _getDocumentTypeOptions() {
    return await Promise.all(
      Object.values(QiraSubject)
        .filter((subject) => DocumentTypeOptions.includes(subject))
        .map(async (documentOption) => ({
          value: documentOption,
          type: 'documentType',
          name: await this.translocoService.translate('source.category.' + documentOption),
        })),
    );
  }

  private async _getProviderOptions() {
    return await Promise.all(
      Object.values(QiraDataProvider)
        .filter((provider) => ProviderOptions.includes(provider))
        .map(async (provider) => ({
          value: provider,
          type: 'provider',
          name: await this.translocoService.translate('source.name.' + provider),
        })),
    );
  }
}
